import { create } from 'zustand';
import type { icModel } from 'generated-ts-common';
import {
  deleteNotification,
  markAllAsRead,
  searchNotifications,
  setNotificationsRead,
  setNotificationsUnread
} from './ajax';
import { PortletStoreApi } from '../App';

type PagingParameter = icModel.saf.infrastructure.model.notification.PagingParameter;

type NotificationSearchParameters = icModel.saf.infrastructure.model.notification.NotificationSearchParameters;

type Notification = icModel.saf.infrastructure.model.notification.Notification;

export const PAGE_SIZE = 20;

export interface NotificationStore {
  loading: boolean;
  additionalNotificationsLoading: boolean;
  notifications: Partial<Notification>[];
  offset: number;
  loadNotifications: (offset: number, reload?: boolean) => Promise<void>;
  totalNotificationCount: number;
  numberOfUnreadNotifications: number;
  active: boolean;
  lastUpdate?: Date;
  init: () => void;
  getNumberOfUnreadNotifications: () => Promise<void>;
  markAllAsRead: () => Promise<void>;
  filter: Partial<NotificationSearchParameters>;
  updateFilter: (filter: Partial<NotificationSearchParameters>) => void;
  deleteNotification: (notification: Partial<Notification>) => Promise<void>;
  markAsRead: (notificationId: string) => Promise<void>;
  markAsUnread: (notificationId: string) => Promise<void>;
  checkForNewNotifications: () => Promise<void>;
}

export const [
  useNotificationStore,
  NotificationStoreApi
] = create<NotificationStore>((set, get) => ({
  loading: false,
  additionalNotificationsLoading: false,
  notifications: [],
  offset: 0,
  totalNotificationCount: 0,
  numberOfUnreadNotifications: 0,
  showOnlyUnread: false,
  filter: {
    channel: 'PORTAL'
  },
  active: false,
  init: () => {
    console.log('init notification list');
    set(
      state =>
        (state.filter = {
          channel: 'PORTAL',
          topicType: PortletStoreApi.getState().configuration.availableTopicTypes?.join(
            ','
          )
        })
    );
  },
  loadNotifications: async (offset: number, reload = false): Promise<void> => {
    if (
      (offset > 0 && offset >= get().totalNotificationCount) || //don't load more if all notifications are loaded
      get().additionalNotificationsLoading || //wait unit current loading is finished
      get().loading
    ) {
      return;
    }
    console.log('loadNotifications', offset, reload);
    if (offset === 0) {
      set(state => (state.notifications = []));
    }
    if (reload) {
      set(state => (state.loading = true));
    } else {
      set(state => (state.additionalNotificationsLoading = true));
    }
    set(state => (state.offset = offset));
    const pagingParam = {
      offset: offset,
      count: PAGE_SIZE
    } as Partial<PagingParameter>;
    try {
      const notifications = await searchNotifications({
        url: PortletStoreApi.getState().url,
        searchParams: {
          ...get().filter,
          pagingParameters: pagingParam as any
        }
      });
      console.log('notifications resp', notifications);
      set(
        state =>
          (state.notifications = [
            ...state.notifications,
            ...notifications.data.notifications
          ])
      );
      set(state => (state.totalNotificationCount = notifications.data.total));
      if (offset === 0 && !reload) {
        //notification center is opened the first time after refresh -> update the number of unread notifications immediately
        await get().getNumberOfUnreadNotifications();
      }
      set(state => (state.active = true));
      set(state => (state.lastUpdate = new Date()));
    } catch (e) {
      console.warn('Could not load notifications', e);
    } finally {
      set(state => (state.additionalNotificationsLoading = false));
      set(state => (state.loading = false));
    }
  },
  getNumberOfUnreadNotifications: async (): Promise<void> => {
    console.log('getNumberOfUnreadNotifications');
    try {
      const pagingParam = {
        offset: 0,
        count: 1000000
      } as any;
      const unreadNotificationCount = await searchNotifications({
        url: PortletStoreApi.getState().url,
        searchParams: {
          pagingParameters: pagingParam,
          channel: 'PORTAL',
          summary: 'count',
          readStatus: false
        }
      });
      console.log('unreadNotificationCount', unreadNotificationCount);
      set(
        state =>
          (state.numberOfUnreadNotifications =
            unreadNotificationCount.data.total)
      );
    } catch (e) {
      console.warn('Could not get the number of unread notifications', e);
    }
  },
  markAllAsRead: async (): Promise<void> => {
    console.log('markAllAsRead');
    try {
      set(state => (state.loading = true));
      await markAllAsRead({
        url: PortletStoreApi.getState().url,
        creationTime: new Date()
      });
      set(state => (state.numberOfUnreadNotifications = 0));
      set(
        state =>
          (state.notifications = [
            ...state.notifications.map(n => ({ ...n, read: true }))
          ])
      );
    } finally {
      set(state => (state.loading = false));
    }
  },
  updateFilter: (filter: Partial<NotificationSearchParameters>) => {
    set(state => (state.filter = { ...state.filter, ...filter }));
    set(state => (state.offset = 0));
    set(state => (state.notifications = []));
    console.log('updateFilter', get().filter);
    get().loadNotifications(0, true);
  },
  deleteNotification: async (
    notification: Partial<Notification>
  ): Promise<void> => {
    console.log('deleteNotification', notification.notificationId);
    try {
      set(state => (state.loading = true));
      await deleteNotification({
        url: PortletStoreApi.getState().url,
        id: notification.notificationId!
      });
      set(
        state =>
          (state.notifications = [
            ...state.notifications.filter(
              n => n.notificationId !== notification.notificationId
            )
          ])
      );
      if (!notification.read) {
        set(
          state =>
            (state.numberOfUnreadNotifications =
              state.numberOfUnreadNotifications - 1)
        );
      }
    } finally {
      set(state => (state.loading = false));
    }
  },
  markAsRead: async (notificationId: string): Promise<void> => {
    console.log('markAsRead', notificationId);
    try {
      set(state => (state.loading = true));
      await setNotificationsRead({
        url: PortletStoreApi.getState().url,
        ids: [notificationId]
      });
      set(
        state =>
          (state.notifications = [
            ...state.notifications.map(n =>
              n.notificationId === notificationId ? { ...n, read: true } : n
            )
          ])
      );
      set(
        state =>
          (state.numberOfUnreadNotifications =
            state.numberOfUnreadNotifications - 1)
      );
    } finally {
      set(state => (state.loading = false));
    }
  },
  markAsUnread: async (notificationId: string): Promise<void> => {
    console.log('markAsUnread', notificationId);
    try {
      set(state => (state.loading = true));
      await setNotificationsUnread({
        url: PortletStoreApi.getState().url,
        ids: [notificationId]
      });
      set(
        state =>
          (state.notifications = [
            ...state.notifications.map(n =>
              n.notificationId === notificationId ? { ...n, read: false } : n
            )
          ])
      );
      set(
        state =>
          (state.numberOfUnreadNotifications =
            state.numberOfUnreadNotifications + 1)
      );
    } finally {
      set(state => (state.loading = false));
    }
  },
  checkForNewNotifications: async (): Promise<void> => {
    console.log('checkForNewNotifications');
    if (get().active) {
      try {
        const notifications = await searchNotifications({
          url: PortletStoreApi.getState().url,
          searchParams: {
            ...get().filter,
            fromDate: new Date(
              get().lastUpdate!.getTime() - 30000
            ).toISOString()
          }
        });
        console.log('new notifications', notifications);
        //filter out already existing notifications
        const newNotifications = notifications.data.notifications.filter(
          n =>
            !get().notifications.find(
              on => on.notificationId === n.notificationId
            )
        );
        if (newNotifications.length > 0) {
          set(
            state =>
              (state.notifications = [
                ...newNotifications,
                ...state.notifications
              ])
          );
        }
      } catch (e) {
        console.warn('Could not check for new notifications', e);
      }
    }
    get().getNumberOfUnreadNotifications(); //we have to always call this as a filter could be applied
  }
}));
